import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { observer } from "mobx-react";
import classnames from "classnames";
import { AgGridReact } from "ag-grid-react";
import {
    ColDef,
    ColGroupDef,
    GridApi,
    GridOptions,
    GridReadyEvent,
    ICellRendererParams,
    PostSortRowsParams,
    RowHeightParams,
    RowClassParams,
    RowClickedEvent,
} from "ag-grid-community";
import { GridTheme } from "modules/common/components/table/GridTheme";
import { DefaultAgGridOptions } from "modules/common/services/table/helpers";
import { agGridLocale } from "modules/root/services/locale";
import { PlanrTag } from "modules/common/components/planr/tag/PlanrTag";
import { GeneralIcon } from "modules/common/components/planr/icon/Generalcon";
import { EmployeeRow, reasonLabels, roleLabels, roleShortLabels, roleTagLabels, YEAR_PERIOD } from "../models/calc";
import { UnitEconomicsStoreType } from "../models/unit-economics-store";
import { hours, percent, rub, rubSign, signedPercent } from "./format";
import { Sparkline } from "./Sparkline";
import { utilizationTone } from "./SummaryPanel";
import { headerTip } from "./headerTip";
import styles from "./UnitEconomicsPage.module.scss";

const NON_PRODUCTION =
    "Непроизводственная должность: затраты сотрудника уже входят в накладные компании, выработка не считается";

type Params = ICellRendererParams<EmployeeRow | TotalRow | DepartmentRow>;

/** Итоговая строка снизу таблицы */
interface TotalRow {
    id: "total";
    name: string;
    total: true;
    workedHours: number;
    projectHours: number;
    availableHours: number;
    factCost: number;
    output: number;
    overhead: number;
    margin: number;
    production: boolean;
}

const isTotal = (data: any): data is TotalRow => !!data?.total;

/** Заголовок отдела при группировке — обычная строка таблицы: суммы стоят в своих колонках */
interface DepartmentRow {
    id: string;
    group: true;
    department: string;
    collapsed: boolean;
    /** В отделе есть производственные — у отдела есть выработка и маржа */
    production: boolean;
    workedHours: number;
    projectHours: number;
    availableHours: number;
    factCost: number;
    output: number;
    overhead: number;
    margin: number;
}

type GridRow = EmployeeRow | DepartmentRow;

const isGroup = (data: any): data is DepartmentRow => !!data?.group;
const groupId = (department: string) => `department:${department}`;

/** Итоговая строка или заголовок отдела — у них только суммы */
const isSummary = (data: any): data is TotalRow | DepartmentRow => isTotal(data) || isGroup(data);

const NameCell = ({ data, context }: Params & { context: GridContext }) => {
    if (!data) {
        return null;
    }

    if (isTotal(data)) {
        return <span className={styles.totalName}>{data.name}</span>;
    }

    if (isGroup(data)) {
        return (
            <span className={styles.groupName}>
                <GeneralIcon
                    type={data.collapsed ? "general-chevron-right" : "general-chevron-down"}
                    size={14}
                    className={styles.groupChevron}
                />
                <span className={styles.groupTitle} title={data.department}>
                    {data.department || "Без отдела"}
                </span>
            </span>
        );
    }

    return (
        <div className={styles.nameCell}>
            <span className={styles.name} title={data.name}>
                {data.shortName}
            </span>
            <span
                className={styles.nameSub}
                title={`${roleTagLabels[data.role]} · ${data.position} · ${data.department}`}
            >
                {/* роль видна по фильтру, тег нужен только в общем списке — и не рядом с ФИО, чтобы не резать фамилию */}
                {context.showRole && data.role !== "production" && (
                    <PlanrTag type={data.role === "gip" ? "primary" : "neutral"} className={styles.roleTag}>
                        {roleShortLabels[data.role]}
                    </PlanrTag>
                )}
                {data.position ? `${data.position} · ` : ""}
                {data.department}
            </span>
        </div>
    );
};

const HoursCell = ({ data }: Params) => {
    if (!data) {
        return null;
    }

    if (isSummary(data)) {
        return <span className={styles.num}>{hours(data.workedHours)}</span>;
    }

    const absence = data.vacationHours + data.illnessHours;
    const tip = [
        `Отработано: ${hours(data.workedHours)} при норме ${hours(data.normHours)}`,
        data.overtimeHours ? `переработка ${hours(data.overtimeHours)}` : "",
        absence ? `отпуск и больничный ${hours(absence)}` : "",
    ]
        .filter(Boolean)
        .join(", ");

    return (
        <span className={styles.num} title={tip}>
            {hours(data.workedHours)}
            {absence > 0 && <span className={styles.absenceDot} />}
        </span>
    );
};

const UtilizationCell = ({ data }: Params) => {
    if (!data) {
        return null;
    }

    const value = data.availableHours > 0 ? (data.projectHours / data.availableHours) * 100 : null;
    if (isSummary(data)) {
        return <span className={styles.num}>{data.projectHours > 0 ? percent(value) : "—"}</span>;
    }

    // непроизводственные часто ведут табель без проектов — ноль здесь значит «не отмечено», а не «простаивал»
    if (!data.production && data.projectHours === 0) {
        return (
            <span className={styles.muted} title="Часы по проектам в табеле не отмечены">
                нет данных
            </span>
        );
    }

    const tone = utilizationTone(value, data.benchmark);
    const scale = Math.max(120, value ?? 0);
    const tip = `На проектах ${hours(data.projectHours)} из доступных ${hours(data.availableHours)}. Цель для роли «${
        roleLabels[data.role]
    }» — ${percent(data.benchmark)}`;

    return (
        <div className={styles.utilCell} title={tip}>
            <span className={styles.utilTrack}>
                <span
                    className={classnames(styles.utilFill, styles[tone])}
                    style={{ width: `${Math.min(100, ((value ?? 0) / scale) * 100)}%` }}
                />
                <span className={styles.utilMarker} style={{ left: `${(data.benchmark / scale) * 100}%` }} />
            </span>
            <span className={classnames(styles.utilValue, styles[tone])}>{percent(value)}</span>
        </div>
    );
};

const HourCostCell = ({ value, data }: Params & { value: number }) => {
    if (!data || isSummary(data)) {
        return null;
    }

    return <span className={styles.num}>{value > 0 ? rubSign(value) : "—"}</span>;
};

const DeviationCell = observer(({ data, context }: Params & { context: GridContext }) => {
    if (!data || isSummary(data)) {
        return null;
    }

    const reason = data.reason;
    const reasonTip = reason
        ? `Причина: ${reason.reasons.map((r) => reasonLabels[r]).join(", ")}${
              reason.comment ? ` — ${reason.comment}` : ""
          }`
        : "";

    const chip = (
        <span
            className={classnames(styles.devChip, {
                [styles.devUp]: data.anomaly && (data.deviation ?? 0) > 0,
                [styles.devDown]: data.anomaly && (data.deviation ?? 0) < 0,
            })}
            title={
                data.deviation === null
                    ? "Нет плановой или фактической стоимости часа"
                    : `Факт ${rub(data.factHour)} ₽ против плана ${rub(data.planHour)} ₽ за час`
            }
        >
            {signedPercent(data.deviation)}
        </span>
    );

    // значок — слева от процента, а процент прижат к правому краю: цифры стоят ровно под заголовком
    return (
        <div className={styles.devCell}>
            {reason ? (
                <button
                    type="button"
                    className={classnames(styles.reasonButton, styles.reasonSet)}
                    title={reasonTip}
                    onClick={(e) => {
                        e.stopPropagation();
                        context.open(data.id, true);
                    }}
                >
                    <GeneralIcon type="general-check" size={12} />
                </button>
            ) : data.anomaly ? (
                <button
                    type="button"
                    className={classnames(styles.reasonButton, styles.reasonMissing)}
                    title="Укажите причину отклонения"
                    onClick={(e) => {
                        e.stopPropagation();
                        context.open(data.id, true);
                    }}
                >
                    <GeneralIcon type="general-exclamation" size={18} />
                </button>
            ) : null}
            {chip}
        </div>
    );
});

const MoneyCell = ({ value, data, colDef }: Params & { value: number }) => {
    if (!data) {
        return null;
    }

    const production = data.production;
    const field = colDef?.field;
    if (!production && field !== "factCost") {
        return (
            <span className={styles.muted} title={NON_PRODUCTION}>
                —
            </span>
        );
    }

    const isMargin = field === "margin";
    return (
        <span
            className={classnames(styles.num, {
                [styles.marginValue]: isMargin,
                [styles.positive]: isMargin && value > 0,
                [styles.negative]: isMargin && value < 0,
            })}
        >
            {rubSign(value)}
        </span>
    );
};

const TrendCell = ({ data, context }: Params & { context: GridContext }) => {
    if (!data || isSummary(data)) {
        return null;
    }

    if (!data.production) {
        return <span className={styles.muted}>—</span>;
    }

    return <Sparkline values={data.trend} active={context.period === YEAR_PERIOD ? undefined : context.period} />;
};

interface GridContext {
    period: number;
    /** Показывать тег роли — только в общем списке */
    showRole: boolean;
    open: (id: string, focusReason?: boolean) => void;
}

const absComparator = (a: number | null, b: number | null) => Math.abs(a ?? 0) - Math.abs(b ?? 0);

const columnDefs = (): (ColDef | ColGroupDef)[] => [
    {
        headerName: "Сотрудник",
        field: "name",
        // у отдела в значение входит «свёрнут/развёрнут» — грид сам перерисует шеврон при переключении
        valueGetter: (p) => (isGroup(p.data) ? `${p.data.department}|${p.data.collapsed}` : p.data?.name),
        // у заголовка отдела название растягивается на «Часы» и «Проекты» — длинные отделы не обрезаются
        colSpan: (p) => (isGroup(p.data) ? 3 : 1),
        cellRenderer: NameCell,
        flex: 1,
        minWidth: 210,
        sortable: true,
        comparator: (a: string, b: string) => a.localeCompare(b),
    },
    {
        headerName: "Загрузка",
        // липкая подпись сжимается по ширине текста и прижимается влево — центрируем над колонками
        suppressStickyLabel: true,
        children: [
            {
                headerName: "Часы",
                field: "workedHours",
                cellClass: ["ag-right-aligned-cell", styles.groupStart],
                headerClass: ["ag-right-aligned-header", styles.groupStart],
                cellRenderer: HoursCell,
                width: 84,
                type: "rightAligned",
                ...headerTip("Отработано по табелю, включая переработки"),
            },
            {
                headerName: "Проекты",
                colId: "utilization",
                field: "utilization",
                cellRenderer: UtilizationCell,
                width: 124,
                ...headerTip("Часы на проектах ÷ доступные часы (норма − отпуск − больничный)"),
                comparator: (a: number | null, b: number | null) => (a ?? -1) - (b ?? -1),
            },
        ],
    },
    {
        headerName: "Стоимость часа, ₽",
        // липкая подпись сжимается по ширине текста и прижимается влево — центрируем над колонками
        suppressStickyLabel: true,
        children: [
            {
                headerName: "План",
                field: "planHour",
                cellClass: ["ag-right-aligned-cell", styles.groupStart],
                headerClass: ["ag-right-aligned-header", styles.groupStart],
                cellRenderer: HourCostCell,
                width: 76,
                type: "rightAligned",
                ...headerTip("Плановые затраты по штатному ÷ норма часов"),
            },
            {
                headerName: "Факт",
                field: "factHour",
                cellRenderer: HourCostCell,
                width: 76,
                type: "rightAligned",
                ...headerTip("Фактические затраты ÷ оплаченные часы (отработано + отпуск + больничный)"),
            },
            {
                headerName: "Откл.",
                field: "deviation",
                cellRenderer: DeviationCell,
                width: 124,
                // как остальные числа — вправо, иначе между группами «дыра» разной ширины
                type: "rightAligned",
                cellClass: ["ag-right-aligned-cell", styles.devColumn],
                comparator: absComparator,
                ...headerTip("Отклонение факта от плана; сортировка — по модулю"),
            },
        ],
    },
    {
        headerName: "Экономика периода, ₽",
        // липкая подпись сжимается по ширине текста и прижимается влево — центрируем над колонками
        suppressStickyLabel: true,
        children: [
            {
                headerName: "Труд",
                field: "factCost",
                cellClass: ["ag-right-aligned-cell", styles.groupStart],
                headerClass: ["ag-right-aligned-header", styles.groupStart],
                cellRenderer: MoneyCell,
                width: 120,
                type: "rightAligned",
                ...headerTip("Себестоимость труда: фактические затраты компании на сотрудника с налогами"),
            },
            {
                headerName: "Выработка",
                field: "output",
                cellRenderer: MoneyCell,
                width: 112,
                type: "rightAligned",
                ...headerTip("Выручка проектов на рубль их затрат × труд сотрудника на проектах"),
            },
            {
                headerName: "Накладные",
                field: "overhead",
                cellRenderer: MoneyCell,
                width: 116,
                type: "rightAligned",
                ...headerTip("Себестоимость труда на проектах × переменная ставка накладных квартала"),
            },
            {
                headerName: "Маржа",
                field: "margin",
                cellRenderer: MoneyCell,
                width: 112,
                type: "rightAligned",
                sort: "desc",
                ...headerTip("Выработка − себестоимость − накладные"),
            },
        ],
    },
    {
        // «Тренд» — в верхней строке шапки, как остальные группы
        headerName: "Тренд",
        suppressStickyLabel: true,
        children: [
            {
                headerName: "I–IV кв.",
                colId: "trend",
                cellRenderer: TrendCell,
                width: 82,
                cellClass: [styles.trendColumn, styles.groupStart],
                headerClass: styles.groupStart,
                sortable: false,
                ...headerTip("Маржа сотрудника по кварталам года"),
            },
        ],
    },
];

export const EconomicsGrid = observer(({ store, onOpen }: EconomicsGridProps) => {
    const rows = store.rows;
    const columns = useMemo(columnDefs, []);
    const options = useMemo<GridOptions>(
        () => ({
            ...DefaultAgGridOptions(true),
            defaultColDef: { cellDataType: false, sortable: true, resizable: true, suppressMovable: true },
        }),
        []
    );

    // AG Grid не перерисовывает ячейки при смене context — держим один объект и обновляем его поля,
    // а ячейки, которые от них зависят, перерисовываем сами
    const showRole = store.segment === "all";
    const context = useRef<GridContext>({ period: store.period, open: onOpen, showRole }).current;
    context.period = store.period;
    context.open = onOpen;
    context.showRole = showRole;

    // причина не входит в значение колонки «Откл.», поэтому грид сам ячейку не перерисует
    const api = useRef<GridApi | null>(null);
    const onGridReady = useCallback((e: GridReadyEvent) => {
        api.current = e.api;
    }, []);
    // группировка по отделам: заголовки отделов — строки на всю ширину, свёрнутые отделы прячут сотрудников
    const grouping = store.groupByDepartment && !store.department;
    const [collapsed, setCollapsed] = useState<Set<string>>(() => new Set());
    const toggleGroup = useCallback(
        (department: string) =>
            setCollapsed((current) => {
                const next = new Set(current);
                next.has(department) ? next.delete(department) : next.add(department);
                return next;
            }),
        []
    );

    const gridRows = useMemo<GridRow[]>(() => {
        if (!grouping) {
            return rows;
        }

        const byDepartment = new Map<string, EmployeeRow[]>();
        rows.forEach((r) => byDepartment.set(r.department, [...(byDepartment.get(r.department) ?? []), r]));

        return Array.from(byDepartment.keys())
            .sort((a, b) => a.localeCompare(b))
            .flatMap((department) => {
                const members = byDepartment.get(department)!;
                const production = members.filter((m) => m.production);
                const isCollapsed = collapsed.has(department);
                const sum = (items: EmployeeRow[], get: (r: EmployeeRow) => number) =>
                    items.reduce((acc, r) => acc + get(r), 0);
                const header: DepartmentRow = {
                    id: groupId(department),
                    group: true,
                    department,
                    collapsed: isCollapsed,
                    production: production.length > 0,
                    workedHours: sum(members, (m) => m.workedHours),
                    projectHours: sum(members, (m) => m.projectHours),
                    availableHours: sum(members, (m) => m.availableHours),
                    factCost: sum(members, (m) => m.factCost),
                    output: sum(production, (m) => m.output),
                    overhead: sum(production, (m) => m.overhead),
                    margin: sum(production, (m) => m.margin),
                };
                return isCollapsed ? [header] : [header, ...members];
            });
    }, [rows, grouping, collapsed]);

    // сортировка по колонке работает внутри отдела: после сортировки собираем строки обратно под заголовки
    const postSortRows = useCallback(
        (params: PostSortRowsParams<GridRow>) => {
            if (!grouping) {
                return;
            }

            const headers = params.nodes.filter((n) => isGroup(n.data));
            const members = new Map<string, typeof params.nodes>();
            params.nodes
                .filter((n) => !isGroup(n.data))
                .forEach((n) => {
                    const department = (n.data as EmployeeRow).department;
                    members.set(department, [...(members.get(department) ?? []), n]);
                });

            const ordered = headers
                .sort((a, b) =>
                    (a.data as DepartmentRow).department.localeCompare((b.data as DepartmentRow).department)
                )
                .flatMap((h) => [h, ...(members.get((h.data as DepartmentRow).department) ?? [])]);

            params.nodes.splice(0, params.nodes.length, ...ordered);
        },
        [grouping]
    );

    const reasons = store.data?.reasons;
    useEffect(() => {
        api.current?.refreshCells({ columns: ["deviation"], force: true });
    }, [reasons]);

    useEffect(() => {
        api.current?.refreshCells({ columns: ["name", "trend"], force: true });
    }, [showRole, store.period]);

    const totalRow = useMemo<TotalRow[]>(() => {
        // часы и труд — по всем строкам; выработка, накладные и маржа есть только у производственных
        const production = rows.filter((r) => r.production);
        const sum = (items: EmployeeRow[], get: (r: EmployeeRow) => number) =>
            items.reduce((acc, r) => acc + get(r), 0);
        return [
            {
                id: "total",
                name: `Итого · ${rows.length} чел.`,
                total: true,
                production: production.length > 0,
                workedHours: sum(rows, (r) => r.workedHours),
                projectHours: sum(rows, (r) => r.projectHours),
                availableHours: sum(rows, (r) => r.availableHours),
                factCost: sum(rows, (r) => r.factCost),
                output: sum(production, (r) => r.output),
                overhead: sum(production, (r) => r.overhead),
                margin: sum(production, (r) => r.margin),
            },
        ];
    }, [rows]);

    const rowClassRules = useMemo(
        () => ({
            [styles.groupLine]: (p: RowClassParams<GridRow>) => isGroup(p.data),
            [styles.rowAnomaly]: (p: RowClassParams<GridRow>) => !isGroup(p.data) && !!p.data?.anomaly,
            [styles.rowNonProduction]: (p: RowClassParams<GridRow>) =>
                !!p.data && !isGroup(p.data) && !p.data.production,
        }),
        []
    );

    const onRowClicked = useCallback(
        (e: RowClickedEvent<GridRow>) => {
            if (!e.data || e.node.rowPinned) {
                return;
            }

            if (isGroup(e.data)) {
                toggleGroup(e.data.department);
                return;
            }

            onOpen(e.data.id, false);
        },
        [onOpen, toggleGroup]
    );

    const getRowHeight = useCallback((p: RowHeightParams<GridRow>) => (isGroup(p.data) ? 44 : 50), []);

    return (
        <GridTheme bordered className={`designed ${styles.grid}`}>
            <AgGridReact<GridRow>
                rowData={gridRows}
                columnDefs={columns}
                gridOptions={options}
                context={context}
                getRowId={(p) => p.data.id}
                pinnedBottomRowData={rows.length ? totalRow : []}
                rowHeight={50}
                getRowHeight={getRowHeight}
                postSortRows={postSortRows}
                headerHeight={34}
                groupHeaderHeight={30}
                rowClassRules={rowClassRules}
                onRowClicked={onRowClicked}
                onGridReady={onGridReady}
                localeText={agGridLocale}
                suppressCellFocus
                enableCellTextSelection
                domLayout="autoHeight"
            />
        </GridTheme>
    );
});

interface EconomicsGridProps {
    store: UnitEconomicsStoreType;
    onOpen: (id: string, focusReason?: boolean) => void;
}
